import React from 'react'
import { getAdDataPanelListNewAPI } from '@/servers/agent-v2'
import { getStores } from '@/stores'
import type { MessageContent } from '@/types/message'
import type { ChatHistoryItem } from '@/servers/agent-v2/types'

const intervalTime = 1000 * 20

const useWsPollingFallback = () => {
  const { agentV2Store } = getStores()
  const pollingTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const pollingActiveRef = React.useRef(false)
  const [newMessageIds, setNewMessageIds] = React.useState<string[]>([])
  const [isPolling, setIsPolling] = React.useState(false)

  const compareMessages = (
    currentRenderMessages: MessageContent[],
    apiMessages: ChatHistoryItem[]
  ): MessageContent[] => {
    const renderMessageIds = new Set(currentRenderMessages.map(msg => String(msg.message_id)))
    const messagesListIds = new Set(agentV2Store.messagesList.map(msg => String(msg.message_id)))
    const existingMessageIds = new Set([...renderMessageIds, ...messagesListIds])

    return apiMessages
      .filter(msg => !existingMessageIds.has(String(msg.message_id)))
      .map(msg => {
        if (msg.role === 'user') {
          return null
        }

        return {
          id: String(msg.message_id),
          message_id: String(msg.message_id),
          placement: 'start' as const,
          isFromHistory: false,
          isFeedback: msg.is_feedback || false,
          content:
            msg.content?.map(item => ({
              ...item,
              styleType: item?.styleType || item?.style_type || 'normal',
              typing: true
            })) || []
        }
      })
      .filter(Boolean) as MessageContent[]
  }

  const executePolling = async () => {
    if (!agentV2Store.currentSessionId) {
      return
    }

    try {
      setIsPolling(true)
      const response = await getAdDataPanelListNewAPI({
        session_id: agentV2Store.currentSessionId,
        is_current: agentV2Store.currentSessionId === agentV2Store.latestSessionId
      })

      if (response?.code === 0 && response?.data?.list) {
        const newMessages = compareMessages(agentV2Store.renderMsgList, response.data.list)

        if (newMessages.length > 0) {
          agentV2Store.messagesList.push(...newMessages)
          setNewMessageIds(prev => [...prev, ...newMessages.map(msg => String(msg.message_id))])
        }
      }
    } catch (error) {
      console.error('useWsPollingFallback: 轮询失败:', error)
    } finally {
      setIsPolling(false)
    }
  }

  const startPolling = () => {
    stopPolling()
    pollingActiveRef.current = true

    const scheduleNext = () => {
      if (!pollingActiveRef.current) return

      pollingTimerRef.current = setTimeout(() => {
        pollingTimerRef.current = null
        if (!pollingActiveRef.current) return

        executePolling().finally(() => {
          if (pollingActiveRef.current) {
            scheduleNext()
          }
        })
      }, intervalTime)
    }

    scheduleNext()
  }

  const stopPolling = () => {
    pollingActiveRef.current = false
    if (pollingTimerRef.current !== null) {
      clearTimeout(pollingTimerRef.current)
      pollingTimerRef.current = null
    }
  }

  const clearNewMessageIds = () => {
    setNewMessageIds([])
  }

  React.useEffect(() => {
    if (agentV2Store.is_onboarding) {
      startPolling()
      return () => {
        stopPolling()
      }
    }

    stopPolling()
    clearNewMessageIds()

    return () => {
      stopPolling()
    }
  }, [agentV2Store.is_onboarding, agentV2Store.currentSessionId, agentV2Store.latestSessionId])

  return {
    newMessageIds,
    isPolling,
    startPolling,
    stopPolling,
    clearNewMessageIds
  }
}

export default useWsPollingFallback
